import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Rating,
  Chip,
  Button,
  Divider,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Skeleton,
  useTheme,
  useMediaQuery,
  alpha,
  styled,
  IconButton,
  Tooltip,
  Badge,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  LocationOn,
  AccessTime,
  Language,
  Psychology,
  Verified,
  Star,
  Phone,
  VideoCall,
  Person,
  Work,
  School,
  Payment,
  Schedule,
  CheckCircle,
  Warning,
  Error,
  LocalHospital,
  EmojiEvents,
  ThumbUp,
  ThumbDown,
  Share,
  Favorite,
  FavoriteBorder,
  Bookmark,
  BookmarkBorder,
  NavigateNext,
  CalendarToday,
  Language as LanguageIcon,
  Psychology as PsychologyIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Payment as PaymentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  LocalHospital as LocalHospitalIcon,
  EmojiEvents as EmojiEventsIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  NavigateNext as NavigateNextIcon,
  CalendarToday as CalendarTodayIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchPublicDoctorProfile, 
  selectCurrentDoctor, 
  selectDoctorLoading, 
  selectDoctorError 
} from '../features/doctors/publicDoctorSlice';
import SEOMetaTags from '../components/SEO/SEOMetaTags';
import QuickBookingWidget from '../components/Booking/QuickBookingWidget';

// Styled Components
const StyledContainer = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(6),
  minHeight: '100vh',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  [theme.breakpoints.down('sm')]: {
    width: 80,
    height: 80,
  },
}));

const HeroSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
  borderRadius: theme.spacing(3),
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'url(/images/pattern.svg)',
    opacity: 0.05,
    zIndex: 0,
  },
}));

const DoctorPublicProfile = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const dispatch = useDispatch();
  const doctor = useSelector(selectCurrentDoctor);
  const loading = useSelector(selectDoctorLoading);
  const error = useSelector(selectDoctorError);
  const [activeTab, setActiveTab] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);

  useEffect(() => {
    if (doctorId) {
      dispatch(fetchPublicDoctorProfile(doctorId));
    }
  }, [dispatch, doctorId]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleBookAppointment = () => {
    navigate(`/book-appointment?doctor=${doctorId}`);
  };

  const handleShare = async () => {
    if (!doctor) return;
    
    const url = `${window.location.origin}/doctor/${doctorId}`;
    const doctorName = doctor.title || `Dr. ${doctor.name || doctor.full_name || 'Doctor'}`;
    
    try {
      if (navigator.share) {
        await navigator.share({
          title: doctor.seo_title || `${doctorName} - PsychePoint`,
          text: doctor.seo_description || `Check out ${doctorName} on PsychePoint`,
          url: url,
        });
      } else {
        await navigator.clipboard.writeText(url);
        // Show success message
      }
    } catch (error) {
      // User cancelled share - this is fine, just log it
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
    }
  };

  const handleFavoriteToggle = () => {
    setIsFavorite(!isFavorite);
  };

  const handleBookmarkToggle = () => {
    setIsBookmarked(!isBookmarked);
  };

  const getAvailabilityColor = (availability) => {
    if (availability === 'Available') return 'success';
    if (availability === 'Limited') return 'warning';
    return 'error';
  };

  const getExperienceText = (years) => {
    if (years === 0) return 'New';
    if (years === 1) return '1 year';
    return `${years} years`;
  };

  if (loading) {
    return (
      <StyledContainer maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </StyledContainer>
    );
  }

  if (error) {
    return (
      <StyledContainer maxWidth="lg">
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      </StyledContainer>
    );
  }

  if (!doctor) {
    return (
      <StyledContainer maxWidth="lg">
        <Alert severity="warning" sx={{ mt: 4 }}>
          Doctor not found
        </Alert>
      </StyledContainer>
    );
  }

  // Generate SEO structured data
  const structuredData = doctor?.structured_data || {
    "@context": "https://schema.org",
    "@type": "Physician",
    "name": doctor?.title || `Dr. ${doctor?.name}`,
    "medicalSpecialty": doctor?.specializations || [],
    "qualification": doctor?.qualifications || [],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": doctor?.rating || 0,
      "reviewCount": doctor?.total_reviews || 0
    },
    "priceRange": `₹${doctor?.consultation_fee || 0}`,
  };

  const handleBookingSuccess = (bookingData) => {
    // Success is handled in QuickBookingWidget with toast notification
    // Just ensure the modal closes properly
    console.log('Booking successful:', bookingData);
  };

  return (
    <>
      <SEOMetaTags
        title={doctor?.seo_title || `Dr. ${doctor?.name || 'Doctor'} - ${doctor?.specializations?.join(', ') || 'Mental Health Professional'}`}
        description={doctor?.seo_description || `Book appointment with ${doctor?.title || `Dr. ${doctor?.name}`}. ${doctor?.experience_text || ''} ${doctor?.specializations?.join(', ') || ''} in ${doctor?.location?.city || 'India'}. Consultation fee ₹${doctor?.consultation_fee || 0}.`}
        keywords={doctor?.seo_keywords || ['mental health', 'psychiatrist', 'psychologist', 'therapist', 'appointment']}
        image={doctor?.photo}
        url={`/doctor/${doctorId}`}
        structuredData={structuredData}
      />

      <StyledContainer maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link color="inherit" href="/" underline="hover">
            Home
          </Link>
          <Link color="inherit" href="/doctors" underline="hover">
            Doctors
          </Link>
          <Typography color="text.primary">{doctor.name || doctor.full_name || doctor.user?.name || 'Doctor'}</Typography>
        </Breadcrumbs>

        {/* Hero Section */}
        <HeroSection>
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} sm={3} md={2}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <StyledAvatar
                    src={doctor.photo || doctor.profile_picture || '/images/default-doctor.jpg'}
                    alt={doctor.name || doctor.full_name || 'Doctor'}
                  >
                    {(doctor.name || doctor.full_name || doctor.user?.name || 'D')?.charAt(0).toUpperCase()}
                  </StyledAvatar>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={9} md={7}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                    {doctor.title || `Dr. ${doctor.name || doctor.full_name || doctor.user?.name || 'Doctor'}`}
                  </Typography>
                  {doctor.specializations && Array.isArray(doctor.specializations) && doctor.specializations.length > 0 && (
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      {doctor.specializations.join(', ')}
                    </Typography>
                  )}
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Rating value={doctor.rating || 0} readOnly precision={0.1} />
                    <Typography variant="body2" color="text.secondary">
                      {doctor.rating || 0} ({doctor.total_reviews || 0} reviews)
                    </Typography>
                    {doctor.is_verified && (
                      <Tooltip title="Verified Doctor">
                        <Badge badgeContent={<Verified sx={{ fontSize: 12 }} />} color="primary">
                          <Box />
                        </Badge>
                      </Tooltip>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {doctor.experience_text && (
                      <Chip
                        icon={<Work />}
                        label={doctor.experience_text || 'Experience not specified'}
                        variant="outlined"
                        size="small"
                      />
                    )}
                    {doctor.location && (
                      <Chip
                        icon={<LocationOn />}
                        label={doctor.location.city && doctor.location.state 
                          ? `${doctor.location.city}, ${doctor.location.state}`
                          : doctor.location.city || doctor.location.state || (typeof doctor.location === 'string' ? doctor.location : 'Location not specified')}
                        variant="outlined"
                        size="small"
                      />
                    )}
                    {doctor.consultation_fee !== undefined && doctor.consultation_fee !== null && (
                      <Chip
                        icon={<Payment />}
                        label={`₹${doctor.consultation_fee}`}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={12} md={3}>
                {/* Quick Booking Widget */}
                <QuickBookingWidget doctor={doctor} onBookingSuccess={handleBookingSuccess} />
                
                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'center' }}>
                  <Tooltip title="Add to Favorites">
                    <IconButton onClick={handleFavoriteToggle} color={isFavorite ? 'error' : 'default'}>
                      {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Bookmark">
                    <IconButton onClick={handleBookmarkToggle} color={isBookmarked ? 'primary' : 'default'}>
                      {isBookmarked ? <BookmarkIcon /> : <BookmarkBorderIcon />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share Profile">
                    <IconButton onClick={handleShare}>
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </HeroSection>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Left Column - Main Content */}
          <Grid item xs={12} lg={8}>
            <StyledCard>
              <CardContent sx={{ p: 0 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  variant="fullWidth"
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <Tab label="Overview" />
                  <Tab label="Reviews" />
                  <Tab label="Availability" />
                </Tabs>
                
                <Box sx={{ p: 3 }}>
                  {/* Overview Tab */}
                  {activeTab === 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Stack spacing={3}>
                        {/* Professional Summary */}
                        {doctor.professional_summary && (
                          <Box>
                            <Typography variant="h6" gutterBottom>
                              About {doctor.title || `Dr. ${doctor.name || doctor.full_name || 'the Doctor'}`}
                            </Typography>
                            <Typography variant="body1" color="text.secondary" paragraph>
                              {doctor.professional_summary}
                            </Typography>
                          </Box>
                        )}

                        {/* Expertise Areas */}
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            Areas of Expertise
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {doctor.expertise_areas.map((area, index) => (
                              <Chip
                                key={index}
                                label={area}
                                color="primary"
                                variant="outlined"
                                icon={<Psychology />}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* Qualifications */}
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            Qualifications
                          </Typography>
                          <List dense>
                            {doctor.qualifications.map((qualification, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  <School color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={qualification} />
                              </ListItem>
                            ))}
                          </List>
                        </Box>

                        {/* Languages */}
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            Languages Spoken
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {doctor.languages.map((language, index) => (
                              <Chip
                                key={index}
                                label={language}
                                variant="outlined"
                                icon={<Language />}
                              />
                            ))}
                          </Box>
                        </Box>

                        {/* Consultation Types */}
                        <Box>
                          <Typography variant="h6" gutterBottom>
                            Consultation Types
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {doctor.consultation_types.map((type, index) => (
                              <Chip
                                key={index}
                                label={type}
                                color="secondary"
                                variant="outlined"
                                icon={type === 'Video' ? <VideoCall /> : type === 'Phone' ? <Phone /> : <Person />}
                              />
                            ))}
                          </Box>
                        </Box>
                      </Stack>
                    </motion.div>
                  )}

                  {/* Reviews Tab */}
                  {activeTab === 1 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Stack spacing={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Typography variant="h6">
                            Patient Reviews
                          </Typography>
                          <Chip
                            label={`${doctor.total_reviews || 0} reviews`}
                            color="primary"
                            variant="outlined"
                          />
                        </Box>

                        {doctor.recent_reviews && Array.isArray(doctor.recent_reviews) && doctor.recent_reviews.length > 0 ? (
                          <Stack spacing={2}>
                            {doctor.recent_reviews.map((review, index) => (
                              <Paper key={index} sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {review.user_name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {review.date}
                                  </Typography>
                                </Box>
                                <Rating value={review.rating} readOnly size="small" sx={{ mb: 1 }} />
                                {review.comment && (
                                  <Typography variant="body2" color="text.secondary">
                                    {review.comment}
                                  </Typography>
                                )}
                              </Paper>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary" textAlign="center">
                            No reviews yet. Be the first to review {doctor.title || `Dr. ${doctor.name || doctor.full_name || 'this doctor'}`}.
                          </Typography>
                        )}
                      </Stack>
                    </motion.div>
                  )}

                  {/* Availability Tab */}
                  {activeTab === 2 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Stack spacing={3}>
                        <Typography variant="h6">
                          Availability
                        </Typography>

                        {doctor.availability && Array.isArray(doctor.availability) && doctor.availability.length > 0 ? (
                          <Grid container spacing={2}>
                            {doctor.availability.slice(0, 7).map((day, index) => (
                              <Grid item xs={12} sm={6} md={4} key={index}>
                                <Paper sx={{ p: 2, textAlign: 'center' }}>
                                  <Typography variant="subtitle2" fontWeight={600}>
                                    {day.day}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {format(new Date(day.date), 'MMM dd')}
                                  </Typography>
                                  <Chip
                                    label="Available"
                                    color="success"
                                    size="small"
                                    sx={{ mt: 1 }}
                                  />
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Typography variant="body2" color="text.secondary" textAlign="center">
                            No availability information available. Please contact the doctor directly.
                          </Typography>
                        )}
                      </Stack>
                    </motion.div>
                  )}
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>

          {/* Right Column - Sidebar */}
          <Grid item xs={12} lg={4}>
            <Stack spacing={3}>
              {/* Quick Info Card */}
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Quick Information
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <LocationOn color="primary" />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Location
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {doctor.location.city}, {doctor.location.state}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Work color="primary" />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Experience
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {doctor.experience_text}
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Payment color="primary" />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          Consultation Fee
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ₹{doctor.consultation_fee}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {doctor.follow_up_fee && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Payment color="primary" />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Follow-up Fee
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ₹{doctor.follow_up_fee}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </StyledCard>

              {/* Next Available */}
              {doctor.next_available && (
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Next Available
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <CalendarToday color="primary" />
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {doctor.next_available.day}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {format(new Date(doctor.next_available.date), 'MMM dd, yyyy')}
                        </Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleBookAppointment}
                      startIcon={<Schedule />}
                    >
                      Book Now
                    </Button>
                  </CardContent>
                </StyledCard>
              )}

              {/* Verification Badges */}
              <StyledCard>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Verification & Trust
                  </Typography>
                  <Stack spacing={2}>
                    {doctor.is_verified && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CheckCircle color="success" />
                        <Typography variant="body2">
                          Verified Doctor
                        </Typography>
                      </Box>
                    )}
                    
                    {doctor.mci_registration && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <LocalHospital color="primary" />
                        <Typography variant="body2">
                          MCI Registered
                        </Typography>
                      </Box>
                    )}
                    
                    {doctor.rci_registration && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Psychology color="primary" />
                        <Typography variant="body2">
                          RCI Registered
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardContent>
              </StyledCard>
            </Stack>
          </Grid>
        </Grid>
      </StyledContainer>
    </>
  );
};

export default DoctorPublicProfile; 